import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Code, Database, Download, Github, Linkedin, Mail, MapPin, Phone, Server, Terminal } from "lucide-react";
import { Link } from "react-router-dom";
import { personalInfo } from "../data/personalInfo";
import { educationData } from "../data/education";
import { experienceData } from "../data/experience";
import { projects } from "../data/projects";
import { skills } from "../data/skills";
import { Reveal } from "../components/Reveal";
import Lanyard from "../components/Lanyard";
import { TiltCard } from "../components/TiltCard";
import { HackerText } from "../components/HackerText";
import { MagneticButton } from "../components/MagneticButton";
import { SectionHeader } from "../components/SectionHeader";
import { ExpCard } from "../components/ExpCard";
import { ProjectCard } from "../components/ProjectCard";

export const HomePage = ({ setHovered, yHero, opacityHero, ageFlow, onSend }) => (
  <>
    <header className="mb-24 flex flex-col md:flex-row justify-between items-end gap-8 relative">
      <motion.div style={{ y: yHero, opacity: opacityHero }}>
        <Reveal>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 border border-gray-200 text-xs font-medium text-gray-600 mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            {personalInfo.relocation}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <h1 className="text-6xl md:text-8xl text-ink leading-[0.9] tracking-tighter mb-5 cursor-default">
            <HackerText text="PRASANNA" className="block md:inline" /><br className="hidden md:block" />
            <HackerText text="DANGOL" />
          </h1>
        </Reveal>

        <Reveal delay={0.16}>
          <div className="h-1 w-24 bg-nothing-red mb-6" />
          <p className="text-lg md:text-xl font-mono text-gray-700 max-w-2xl">
            {personalInfo.role} <span className="text-gray-300 mx-2">//</span> {personalInfo.subRole}
          </p>
        </Reveal>

        <Reveal delay={0.22}>
          <p className="text-base text-gray-500 max-w-xl mt-4 leading-relaxed">{personalInfo.heroLine}</p>
        </Reveal>

        <Reveal delay={0.3}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-10 border-t border-gray-200 pt-8 max-w-2xl">
            {personalInfo.stats.map((s) => (
              <div key={s.label}>
                <div className="text-2xl font-ndot text-ink">{s.value}</div>
                <div className="text-[10px] font-mono tracking-widest text-gray-400 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
        className="flex flex-col items-end gap-3 z-10"
      >
        <div style={{ width: "320px", height: "520px" }}>
          <Lanyard frontImage="/profileImg.png" size={0.7} strapLength={0.65} strapColor="#9aa0a6" metal="silver" />
        </div>
        <div className="flex gap-3">
          <MagneticButton setHovered={setHovered} className="p-4 rounded-full border border-gray-200 bg-white hover:bg-ink hover:text-white transition-all">
            <a href={personalInfo.github} target="_blank" rel="noreferrer"><Github size={20} /></a>
          </MagneticButton>
          <MagneticButton setHovered={setHovered} className="p-4 rounded-full border border-gray-200 bg-white hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2] transition-all">
            <a href={personalInfo.linkedin} target="_blank" rel="noreferrer"><Linkedin size={20} /></a>
          </MagneticButton>
          <MagneticButton
            setHovered={setHovered}
            onClick={() => window.open(personalInfo.resume, "_blank")}
            className="px-6 py-4 rounded-full border border-gray-200 bg-white hover:bg-nothing-red hover:text-white hover:border-nothing-red transition-all flex items-center gap-2 font-bold text-sm"
          >
            <Download size={18} /> RESUME_PDF
          </MagneticButton>
        </div>
        <div className="text-right text-xs font-mono text-gray-500 mt-2 tracking-wider">
          <span className="text-nothing-red">::</span> AGE_<span className="text-ink font-bold">{ageFlow}</span>
          <span className="animate-pulse">_</span>
          <span className="ml-4 text-gray-400">•</span> {personalInfo.city}
        </div>
      </motion.div>
    </header>

    <Reveal>
      <div className="mb-24 -mx-4 md:-mx-6 border-y border-gray-200 bg-white py-4 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee">
          {Array(6).fill(" ").map((_, i) => (
            <React.Fragment key={i}>
              <span className="text-4xl font-ndot text-gray-300 mx-8 cursor-default inline-block transition-transform duration-200 hover:scale-125 hover:text-nothing-red">REACT</span>
              <span className="text-4xl font-ndot text-ink mx-8 cursor-default inline-block transition-transform duration-200 hover:-rotate-3 hover:scale-110 hover:text-nothing-red">NODE.JS</span>
              <span className="text-4xl font-ndot text-gray-300 mx-8 cursor-default inline-block transition-transform duration-200 hover:scale-125 hover:text-nothing-red">MONGODB</span>
              <span className="text-4xl font-ndot text-ink mx-8 cursor-default inline-block transition-transform duration-200 hover:-rotate-3 hover:scale-110 hover:text-nothing-red">NEXT.JS</span>
              <span className="text-4xl font-ndot text-gray-300 mx-8 cursor-default inline-block transition-transform duration-200 hover:scale-125 hover:text-nothing-red">DEVOPS</span>
              <span className="text-4xl font-ndot text-nothing-red mx-8 cursor-default inline-block transition-transform duration-200 hover:scale-125 hover:text-ink">AUTOMATION</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    </Reveal>

    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-24">
      <div className="md:col-span-8">
        <Reveal delay={0.1}>
          <div className="bg-white border border-gray-200 p-8 rounded-xl h-full shadow-sm hover:shadow-md transition-shadow">
            <SectionHeader title="BIO_DATA" icon={Terminal} index={1} />
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Fullstack Developer with <span className="font-bold bg-yellow-100 px-1">3+ years of experience</span> building scalable web and mobile applications for startups and SMEs. Proven ability to deliver production-ready products, lead small teams, and work directly with international clients.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Strong focus on <span className="text-nothing-red font-medium">full-stack engineering</span>, backend systems, automation workflows (n8n), and offline-first architectures.
            </p>
          </div>
        </Reveal>
      </div>
      <div className="md:col-span-4">
        <Reveal delay={0.2}>
          <TiltCard>
            <div className="bg-gray-50 border border-gray-200 p-8 rounded-xl flex flex-col justify-between h-full">
              <div>
                <div className="flex items-center gap-2 mb-4 text-gray-400">
                  <MapPin size={16} />
                  <span className="text-xs font-mono uppercase">{educationData.location}</span>
                </div>
                <h3 className="text-xl font-bold leading-tight mb-2">{educationData.school}</h3>
                <p className="text-sm text-gray-600">{educationData.degree}</p>
                <p className="text-xs text-gray-400 mt-1 font-mono">{educationData.year}</p>
              </div>
              <div className="border-t border-gray-200/70 mt-8 pt-5">
                <span className="block text-[10px] font-mono tracking-widest text-gray-400 mb-3">SCHOOLING</span>
                <div className="flex items-center gap-2 mb-3 text-gray-400">
                  <MapPin size={12} />
                  <span className="text-[10px] font-mono uppercase">{educationData.schooling.location}</span>
                </div>
                <h3 className="text-xs font-bold leading-tight mb-1">{educationData.schooling.school}</h3>
                <p className="text-[10px] text-gray-400 font-mono">{educationData.schooling.year}</p>
              </div>
            </div>
          </TiltCard>
        </Reveal>
      </div>
    </div>

    <section className="mb-24">
      <Reveal>
        <SectionHeader title="SYSTEM_LOGS" icon={Server} index={2} />
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          {experienceData.map((exp) => (
            <ExpCard key={exp.id} data={exp} setHovered={setHovered} />
          ))}
        </div>
      </Reveal>
    </section>

    <section className="mb-10">
      <Reveal>
        <SectionHeader title="SCHEMATICS" icon={Code} index={3} />
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
        {projects.map((project, idx) => (
          <Reveal key={project.id} delay={0.1 + idx * 0.08} className={`h-full ${idx === 0 ? "lg:col-span-2" : ""}`}>
            <ProjectCard project={project} setHovered={setHovered} />
          </Reveal>
        ))}
      </div>
    </section>

    <Reveal>
      <Link
        to="/gyanudaan"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="group mb-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 rounded-3xl bg-ink p-6 md:p-8 text-white hover:shadow-xl transition-shadow"
      >
        <div>
          <p className="font-mono text-[10px] tracking-[0.2em] text-white/50">CASE STUDY 01 · GYANUDAAN · 40K+ USERS · 344K+ LOC</p>
          <h3 className="mt-2 font-ndot text-2xl md:text-3xl">The Engineering Behind the GyanUdaan App — full breakdown</h3>
          <p className="mt-1 font-mono text-xs text-white/50">20 mobile screens · 12 admin consoles · Lead 360 CRM · AI pipeline</p>
        </div>
        <span className="flex shrink-0 items-center gap-2 rounded-full bg-nothing-red px-6 py-3.5 text-xs font-bold uppercase tracking-wider transition-colors group-hover:bg-white group-hover:text-ink">
          Open case study <ArrowUpRight size={16} />
        </span>
      </Link>
    </Reveal>

    <section className="mb-24">
      <Reveal>
        <SectionHeader title="TECH_SPECS" icon={Database} index={4} />
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(skills).map(([category, items], idx) => (
          <Reveal key={category} delay={idx * 0.1}>
            <div className="bg-white border border-gray-200 p-6 rounded-xl hover:shadow-md transition-shadow group h-full relative overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-0.5 bg-nothing-red scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
              <div className="flex items-center justify-between mb-5">
                <h4 className="font-ndot text-gray-500 uppercase group-hover:text-ink transition-colors">{category}</h4>
                <span className="text-[10px] font-mono text-gray-400">{items.length.toString().padStart(2, "0")}</span>
              </div>
              <ul className="space-y-2.5">
                {items.map((skill) => (
                  <li key={skill} className="flex items-center gap-2.5 text-sm font-mono text-gray-600 hover:text-ink transition-colors">
                    <div className="w-1 h-1 bg-gray-300 rounded-full group-hover:bg-nothing-red transition-colors" />
                    {skill}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <footer className="border-t border-gray-200 pt-16 md:pt-24 pb-8">
      <Reveal>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-12">
          <div>
            <h2 className="text-4xl md:text-6xl font-ndot text-ink mb-4">INITIALIZE<br />COLLABORATION</h2>
            <p className="text-gray-500 mb-8 max-w-md leading-relaxed">
              Have a role or project in mind? I'm happy to jump on a call — let's build something great together.
            </p>
            <div className="flex flex-col gap-4">
              <a href={`mailto:${personalInfo.email}`} className="flex items-center gap-3 text-lg hover:text-nothing-red transition-colors group">
                <div className="p-2 bg-gray-100 rounded-md group-hover:bg-nothing-red group-hover:text-white transition-colors">
                  <Mail size={20} />
                </div>
                {personalInfo.email}
              </a>
              <div className="flex items-center gap-3 text-lg text-gray-600">
                <div className="p-2 bg-gray-100 rounded-md"><Phone size={20} /></div>
                {personalInfo.phone}
              </div>
            </div>
          </div>
          <div className="flex gap-4">
            <MagneticButton
              setHovered={setHovered}
              onClick={onSend}
              className="px-8 py-4 bg-ink text-white rounded-full font-bold hover:bg-nothing-red transition-colors"
            >
              <a href={`mailto:${personalInfo.email}`}>SEND TRANSMISSION</a>
            </MagneticButton>
          </div>
        </div>
      </Reveal>
      <div className="mt-24 flex justify-between items-center text-xs font-mono text-gray-500 uppercase">
        <span>© {new Date().getFullYear()} Prasanna Dangol</span>
        <span>{personalInfo.location}</span>
      </div>
    </footer>
  </>
);
